import { useState } from "react";
import { sanitizeResponse } from "../utils/sanitize";
import { scanOutputForIssues } from "../utils/serviceValidation";
import {
  Container,
  SpaceBetween,
  Textarea,
  Button,
  FormField,
  Alert,
} from "@cloudscape-design/components";
import { bihGenerateClient } from "../utils/bihClient";
import { qc3ReviewPrompts } from "../config/prompts";

const invokeBedrock = /* GraphQL */ `
  query InvokeBedrock($prompt: String!) {
    invokeBedrock(prompt: $prompt) {
      body
      statusCode
    }
  }
`;

const agenticReviewQuery = /* GraphQL */ `
  query AgenticReview($prompt: String!) {
    agenticReview(prompt: $prompt) {
      body
      statusCode
    }
  }
`;

export default function QC3Input() {
  const [itemText, setItemText] = useState("");
  const [teQuestions, setTeQuestions] = useState("");
  const [outputData, setOutputData] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [findings, setFindings] = useState([]);
  // X-BIH-Tab header labels QC3 invocations distinctly from "general"
  // (E-today-3 fix; Lambda allowlist regex /^[a-z0-9-]{1,40}$/).
  const client = bihGenerateClient({ tab: "qc3" });

  async function handleSubmit() {
    setIsLoading(true);
    setOutputData("Waiting for response...");
    setFindings([]);

    const prompt =
      qc3ReviewPrompts.systemPrompt +
      itemText +
      "\n\n## TE QUESTIONS ##\n\n" +
      teQuestions;

    try {
      const agenticPrompt =
        qc3ReviewPrompts.systemPrompt +
        "\n\n## ITEM FOR REVIEW ##\n\n" +
        itemText +
        "\n\n## TE QUESTIONS ##\n\n" +
        teQuestions;
      const response = await client.graphql({
        query: agenticReviewQuery,
        variables: { prompt: agenticPrompt },
      });
      const result = response.data.agenticReview;
      if (result && result.statusCode === 200) {
        const sanitized = sanitizeResponse(result.body);
        setOutputData(sanitized);
        setFindings(scanOutputForIssues(sanitized));
      } else {
        setOutputData(`Error: ${result?.body || "Empty response"}`);
      }
    } catch (error) {
      setOutputData(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(outputData);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <Container>
      <SpaceBetween size="l">
        <FormField label="Paste the full item (stem + responses + rationale)" stretch>
          <Textarea
            value={itemText}
            onChange={({ detail }) => setItemText(detail.value)}
            placeholder="Paste the item here (stem, responses A-D/E/F, key, rationale)"
            rows={10}
          />
        </FormField>
        <FormField label="TE Questions" stretch>
          <Textarea
            value={teQuestions}
            onChange={({ detail }) => setTeQuestions(detail.value)}
            placeholder="Paste the TE questions from the Notes field"
            rows={5}
          />
        </FormField>
        <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={isLoading}
            loadingText="Generating response..."
            disabled={!itemText || !teQuestions}
          >
            Ask AI
          </Button>
        </div>
        <FormField label="AI Response" stretch>
          <Textarea
            value={outputData}
            onChange={({ detail }) => setOutputData(detail.value)}
            placeholder="AI response will appear here"
            rows={10}
            readOnly={isLoading}
          />
        </FormField>
        {findings.length > 0 && !isLoading && (
          <Alert
            type="warning"
            header={`${findings.length} potential issue${findings.length === 1 ? "" : "s"} detected in the response`}
          >
            <ul style={{ marginTop: 0, marginBottom: 0, paddingLeft: "1.5em" }}>
              {findings.map((f, i) => (
                <li key={i}>{f.message}</li>
              ))}
            </ul>
          </Alert>
        )}
        {outputData && !isLoading && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={copyToClipboard} iconName="copy">
              Copy response
            </Button>
          </div>
        )}
      </SpaceBetween>
    </Container>
  );
}
